/**
 * SearchRequestManager
 * Helper dùng chung để quản lý các request tìm kiếm bất đồng bộ:
 * 1. Chống Race Condition: Sử dụng sequence ID để chỉ chấp nhận phản hồi từ request mới nhất,
 *    bỏ qua các phản hồi trả về chậm của request cũ (ví dụ request tải cả danh sách ban đầu).
 * 2. Tối ưu Submit: Hủy debounce đang chờ, tự động reset phân trang về trang 1, và kích hoạt
 *    tìm kiếm ngay lập tức khi người dùng nhấn Enter hoặc click nút Tìm kiếm.
 */
export interface SearchSubmitOptions {
  /** Hàm debounce cần hủy bỏ bộ đếm thời gian đang chờ (nếu có) */
  debounceFn?: { cancel?: () => void };
  /** Callback reset số trang về 1 */
  resetPage?: () => void;
  /** Callback kích hoạt tìm kiếm (gọi hàm fetch hoặc table.refresh()) */
  execute: () => void | Promise<any>;
}

export class SearchRequestManager {
  private currentRequestId: number = 0;
  private lastSubmitTime: number = 0;

  /**
   * Sinh và trả về ID cho request mới nhất
   */
  public nextId(): number {
    return ++this.currentRequestId;
  }

  /**
   * Kiểm tra xem ID của request có còn là request mới nhất hay không
   * @param requestId ID của request cần kiểm tra
   */
  public isLatest(requestId: number): boolean {
    return requestId === this.currentRequestId;
  }

  /**
   * Đặt lại sequence ID và thời gian submit về 0
   */
  public reset(): void {
    this.currentRequestId = 0;
    this.lastSubmitTime = 0;
  }

  /**
   * Xử lý submit tìm kiếm:
   * - Chống spam click (Leading Throttle với cooldown mặc định 300ms):
   *   Thực thi NGAY LẬP TỨC ở lần bấm đầu tiên (0ms delay), và bỏ qua các lần click liên tiếp trong khoảng cooldown.
   * - Hủy debounce đang chờ (nếu có).
   * - Reset số trang về 1.
   * - Thực thi tìm kiếm ngay lập tức.
   *
   * @param options Cấu hình submit gồm debounceFn, resetPage, execute
   * @param cooldownMs Thời gian tối thiểu giữa 2 lần submit liên tiếp (mặc định 300ms)
   */
  public onSubmit(options: SearchSubmitOptions, cooldownMs: number = 300): void {
    const now = Date.now();
    if (now - this.lastSubmitTime < cooldownMs) {
      // Đang trong thời gian cooldown chống spam click -> Bỏ qua request spam
      return;
    }
    this.lastSubmitTime = now;

    options.debounceFn?.cancel?.();
    options.resetPage?.();
    options.execute();
  }
}
